import type { AgentRole, BobState } from "../types";
import {
  advanceStage,
  assignRole,
  pauseDirective,
  resumeDirective,
  STAGE_LABELS,
  syncAgentsWithDirectives,
  updateDirective,
} from "../lib/engine/pipeline";

interface Props {
  state: BobState;
  onUpdate: (partial: Partial<BobState>) => void;
  toast: (msg: string) => void;
}

const ROLES: AgentRole[] = ["bob", "felix", "lisa", "executor"];

export function PipelinePanel({ state, onUpdate, toast }: Props) {
  const compact = state.presentationMode;

  function patchDirective(id: string, updater: Parameters<typeof updateDirective>[2]) {
    const directives = updateDirective(state.directives, id, updater);
    onUpdate({
      directives,
      agents: syncAgentsWithDirectives(state.agents, directives),
    });
  }

  function handleAdvance(id: string) {
    patchDirective(id, advanceStage);
    toast(compact ? "Stage advanced" : "Directive advanced to next pipeline stage");
  }

  function handlePause(id: string, paused: boolean) {
    patchDirective(id, paused ? resumeDirective : pauseDirective);
    toast(paused ? "Directive resumed" : "Directive paused");
  }

  function handleAssign(id: string, role: AgentRole) {
    patchDirective(id, (d) => assignRole(d, role));
    toast(`Assigned to ${role}`);
  }

  return (
    <section className="surface glass-panel">
      <h2>{compact ? "Pipeline" : "Pipeline Board"}</h2>
      <p className="lead">
        {compact
          ? "Advance, pause, assign roles."
          : "Simulated build pipeline — advance stages, pause/resume, and assign agent roles per directive."}
      </p>

      {state.directives.length === 0 ? (
        <p className="lead">{compact ? "No directives." : "No directives yet — register an intake to create one."}</p>
      ) : (
        <div className="pipeline-list">
          {state.directives.map((d) => (
            <article key={d.id} className={`pipeline-card ${d.paused ? "paused" : ""}`}>
              <div className="pipeline-card-head">
                <h3>{d.name}</h3>
                <span className={`chip ${d.paused ? "locked" : "ok"}`}>
                  {d.paused ? "paused" : STAGE_LABELS[d.stage]}
                </span>
              </div>
              {!compact && <p className="pipeline-objective">{d.objective}</p>}
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${d.progress}%` }} />
              </div>
              <div className="pipeline-meta">
                <span className="chip">{d.priority}</span>
                <span className="chip">{d.assignedRole}</span>
                <span className="chip">{d.progress}%</span>
              </div>
              <div className="btn-row">
                <button type="button" className="btn" disabled={d.paused} onClick={() => handleAdvance(d.id)}>
                  Advance
                </button>
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => handlePause(d.id, d.paused)}
                >
                  {d.paused ? "Resume" : "Pause"}
                </button>
                <select
                  className="role-select"
                  value={d.assignedRole}
                  onChange={(e) => handleAssign(d.id, e.target.value as AgentRole)}
                  aria-label={`Assign role for ${d.name}`}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
